/**
 * First-login welcome tour — an 8-slide modal that introduces the basics
 * of Rex Mortis. Completion is tracked per-account in localStorage. Mount
 * it from the router after the user reaches the first authenticated page.
 */

import { store } from '../state/store.js';

interface TourSlide {
  ornament: string;
  title: string;
  body: string;
  cta?: string;
  /** When the user clicks "Visit ...", navigate here. Optional. */
  link?: string;
}

const SLIDES: TourSlide[] = [
  {
    ornament: '⚜',
    title: 'Welcome to Rex Mortis',
    body:
      'A medieval kingdom of crime, combat, commerce, and conspiracy. ' +
      'You\'ve been given a name, a body, four resources that regenerate over time, and a whole realm of things to do with them. ' +
      'This brief tour points you at the ones that matter most on day one — and a step-by-step guide will follow you in the corner afterward.',
  },
  {
    ornament: '⚔',
    title: 'Train, then fight',
    body:
      'Visit the <strong>Training Yard</strong> to spend Vigor on raising Strength, Defense, Speed, and Dexterity. ' +
      'Each yard tier multiplies the stats you gain — but the higher tiers demand higher minimum stats to enter. ' +
      'Once you have a few hundred points, head to the <strong>Sparring Hall</strong> to test yourself against NPCs and earn XP.',
    cta: 'Visit Training Yard',
    link: '#/training',
  },
  {
    ornament: '☠',
    title: 'Crime — or virtue',
    body:
      'The <strong>Misdeeds</strong> page lists dozens of ways to break the law for crowns, items, and infamy — ' +
      'each rolls Triumph, Partial, Foiled, or Disastrous, and a Disastrous roll lands you in the Dungeon, so build ' +
      'Cunning before the harder jobs. Prefer the honest road? <strong>Good Deeds</strong> mirror them for crowns, ' +
      'Virtue, and Honour (a foiled deed risks the Infirmary instead).',
    cta: 'Visit Misdeeds',
    link: '#/misdeeds',
  },
  {
    ornament: '⛁',
    title: 'Keep your gold safe',
    body:
      'Wallet crowns can be stolen by other players in PvP. Deposit anything you\'re not actively spending in the ' +
      '<strong>Counting House</strong> — it accrues interest at your tier\'s daily rate and is immune to plunder. ' +
      'You can also list spare items on the <strong>Merchant\'s Market</strong> for passive income.',
    cta: 'Visit Counting House',
    link: '#/banks',
  },
  {
    ornament: '⛵',
    title: 'Sail, race, and aid the realm',
    body:
      'When you\'re ready to roam, <strong>The Roads</strong> let you sail to foreign kingdoms for goods you can\'t buy at home — ' +
      'buy a finer ship at the <strong>Royal Shipyard</strong> to shorten every crossing. Raise and race horses at the ' +
      '<strong>Racing Yard</strong> for crowns and prestige. And you can pull other players out of the Infirmary or Dungeon — ' +
      'each rescue sharpens your <strong>Leechcraft</strong> or <strong>Gaolcraft</strong> and costs you less the next time.',
    cta: 'Set sail',
    link: '#/travel',
  },
  {
    ornament: '⚒',
    title: 'Live off the land',
    body:
      'Every foreign city offers a <strong>field trade</strong> at its own named spot — fish the black mirror of Loch Clyde, ' +
      'mine the Iron Pits of Khazan, stalk the high moors. Eight trades in all, each levelling on its own, and bare hands ' +
      'work fine until you can afford a tool. Haul your takings home to the <strong>Forge</strong>, <strong>Apothecary</strong>, ' +
      '<strong>Cookfire</strong>, and <strong>Brewhouse</strong> — gear, remedies, dishes, and drink, with the finest recipes ' +
      'reserved for those who master a crafting vocation at the Trades hall.',
    cta: 'See the Forge',
    link: '#/forge',
  },
  {
    ornament: '☽',
    title: 'Stand together — and beware the dark',
    body:
      'Each week a denizen rises in <strong>The Crypt</strong>, and the whole realm fells it together — every strike counts, ' +
      'no matter your level, and the slain beast pays out by the damage you dealt. But know this: glory draws the dark. ' +
      'The realm\'s three mightiest are hunted by <strong>the Shade</strong>, and the prudent may buy its mercy at ' +
      '<strong>The Shade\'s Due</strong> — for a price the whole realm gets to read about.',
    cta: 'Enter the Crypt',
    link: '#/crypt',
  },
  {
    ornament: '✉',
    title: 'Talk to the world',
    body:
      'Use <strong>The Post</strong> to send mail to other players, join a <strong>House</strong> for protection and shared treasury, ' +
      'and watch the bell ✉ at the top right for incoming attacks and messages. ' +
      'Climb <strong>The Annals</strong> to see how you measure against the realm.',
  },
];

const STORAGE_KEY_PREFIX = 'rex_mortis_tour_completed_';

function storageKey(): string {
  const account = store.get().account;
  return STORAGE_KEY_PREFIX + (account?.id ?? 'anon');
}

export function hasCompletedTour(): boolean {
  try {
    return localStorage.getItem(storageKey()) === '1';
  } catch {
    // localStorage unavailable (private mode) — treat as completed so we don't pester.
    return true;
  }
}

function markCompleted(): void {
  try {
    localStorage.setItem(storageKey(), '1');
  } catch { /* ignore */ }
}

/** Show the tour modal. Returns a Promise that resolves when the user closes it. */
export function showWelcomeTour(): Promise<void> {
  return new Promise<void>((resolve) => {
    let slide = 0;

    const overlay = document.createElement('div');
    overlay.className = 'tour-overlay';
    document.body.append(overlay);

    const modal = document.createElement('div');
    modal.className = 'tour-modal';
    document.body.append(modal);

    // Inject styles once.
    if (!document.getElementById('tour-styles')) {
      const style = document.createElement('style');
      style.id = 'tour-styles';
      style.textContent = TOUR_STYLES;
      document.head.append(style);
    }

    const close = (): void => {
      markCompleted();
      overlay.remove();
      modal.remove();
      resolve();
    };

    const render = (): void => {
      const s = SLIDES[slide];
      if (!s) return;
      const isLast = slide === SLIDES.length - 1;
      modal.innerHTML = `
        <div class="tour-ornament">${s.ornament}</div>
        <h2 class="tour-title">${s.title}</h2>
        <p class="tour-body">${s.body}</p>
        <div class="tour-pips">
          ${SLIDES.map((_, i) => `<span class="tour-pip ${i === slide ? 'active' : ''}"></span>`).join('')}
        </div>
        <div class="tour-actions">
          <button class="btn ghost" id="tour-skip">${isLast ? 'Close' : 'Skip tour'}</button>
          ${
            s.cta && s.link
              ? `<button class="btn ghost" id="tour-visit">${s.cta}</button>`
              : ''
          }
          ${
            isLast
              ? '<button class="btn" id="tour-done">Done</button>'
              : '<button class="btn" id="tour-next">Next →</button>'
          }
        </div>
      `;

      modal.querySelector<HTMLButtonElement>('#tour-skip')?.addEventListener('click', close);
      modal.querySelector<HTMLButtonElement>('#tour-done')?.addEventListener('click', close);
      modal.querySelector<HTMLButtonElement>('#tour-next')?.addEventListener('click', () => {
        slide = Math.min(SLIDES.length - 1, slide + 1);
        render();
      });
      modal.querySelector<HTMLButtonElement>('#tour-visit')?.addEventListener('click', () => {
        const link = SLIDES[slide]?.link;
        if (link) {
          close();
          // navigate via hash
          window.location.hash = link.startsWith('#') ? link.slice(1) : link;
        }
      });
    };

    render();
  });
}

const TOUR_STYLES = `
  .tour-overlay {
    position: fixed;
    inset: 0;
    background: rgba(28, 17, 8, 0.65);
    backdrop-filter: blur(2px);
    z-index: 200;
  }
  .tour-modal {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: min(540px, 92vw);
    background: var(--c-parchment);
    background-image: var(--parchment-texture);
    border: 1px solid var(--c-rule-strong);
    border-radius: var(--radius-lg);
    box-shadow:
      0 0 0 1px var(--c-gold),
      0 18px 60px rgba(0, 0, 0, 0.55);
    padding: var(--s-6) var(--s-5);
    z-index: 201;
    text-align: center;
  }
  .tour-ornament {
    font-family: var(--font-display);
    font-size: 2.4rem;
    color: var(--c-gold);
    line-height: 1;
    margin-bottom: var(--s-3);
    text-shadow: 0 1px 0 rgba(255, 245, 220, 0.5);
  }
  .tour-title {
    font-family: var(--font-heading);
    font-weight: 700;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    font-size: 1.55rem;
    margin: 0 0 var(--s-3) 0;
    color: var(--c-oxblood);
  }
  .tour-body {
    font-family: var(--font-body);
    font-size: 1.05rem;
    line-height: 1.55;
    color: var(--c-ink);
    margin: 0 0 var(--s-4) 0;
    text-align: left;
  }
  .tour-body strong { color: var(--c-oxblood); }
  .tour-pips {
    display: flex;
    justify-content: center;
    gap: 8px;
    margin-bottom: var(--s-4);
  }
  .tour-pip {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--c-rule-soft);
    border: 1px solid var(--c-rule);
    transition: background 0.2s ease;
  }
  .tour-pip.active {
    background: var(--c-gold);
    border-color: #6e5510;
  }
  .tour-actions {
    display: flex;
    justify-content: center;
    gap: var(--s-2);
    flex-wrap: wrap;
  }
`;
